import { KsAppClientDataNoMeta } from '@knapsack/types';
import { makeShortId, type Draft } from '@knapsack/utils';
import {
  duplicateBlockCollection,
  duplicateBlocks,
} from './utils.blocks.xstate';

/**
 * Duplicates a tab and its block collections.
 */
export function duplicateTab({
  pageId,
  tabId,
  data,
}: {
  /**
   * If provided, the new tab will be inserted into the page after the original tab.
   */
  pageId?: string;
  tabId: string;
  data: Draft<KsAppClientDataNoMeta>;
}): {
  newTabId: string;
  newBlockCollectionIds: string[];
  newBlockIds: string[];
} {
  const tab = data.db.tabs.byId[tabId];
  if (!tab) {
    throw new Error(`Tab not found: ${tabId}`);
  }

  const newBlockCollectionIds: string[] = [];
  const newBlockIds: string[] = [];
  for (const blockCollectionId of tab.blockCollectionIds) {
    const { newBlockCollectionId, newBlockIds: newBlockIdsForCollection } =
      duplicateBlockCollection({
        blockCollectionId,
        data,
      });
    newBlockCollectionIds.push(newBlockCollectionId);
    newBlockIds.push(...newBlockIdsForCollection);
  }
  const newTabId = makeShortId();

  if (pageId) {
    const page = data.customPagesState.pages[pageId];
    if (!page) {
      throw new Error(`Page not found: ${pageId}`);
    }

    // ensure unique tab titles and slugs
    const getNextNumber = (baseTitle: string) => {
      // Get all tab titles that start with the base title
      const existingTabs = Object.values(data.db.tabs.byId).filter((t) =>
        t.title.startsWith(baseTitle.replace(/\s\d+$/, '')),
      );

      // Extract numbers from existing titles
      const numbers = existingTabs.map((t) => {
        const match = t.title.match(/\s(\d+)$/);
        return match ? parseInt(match[1], 10) : 1;
      });

      // Return next available number
      return numbers.length > 0 ? Math.max(...numbers) + 1 : 2;
    };

    const baseTitle = tab.title.replace(/\s\d+$/, '');
    const nextNumber = getNextNumber(baseTitle);
    const newTabTitle = `${baseTitle} ${nextNumber}`;
    const newTabSlug = `${tab.slug.replace(/-\d+$/, '')}-${nextNumber}`;
    const newTab = {
      ...tab,
      id: newTabId,
      title: newTabTitle,
      slug: newTabSlug,
      blockCollectionIds: newBlockCollectionIds,
    };
    data.db.tabs.byId[newTabId] = newTab;

    // insert the new tabId right after the original tabId
    const indexOfTab = page.tabIds.indexOf(tabId);
    page.tabIds.splice(indexOfTab + 1, 0, newTabId);
  } else {
    data.db.tabs.byId[newTabId] = {
      ...tab,
      id: newTabId,
      blockCollectionIds: newBlockCollectionIds,
    };
  }
  return { newTabId, newBlockCollectionIds, newBlockIds };
}

export function duplicatePage({
  pageId,
  newId,
  newTitle,
  data,
}: {
  pageId: string;
  newId: string;
  newTitle: string;
  data: Draft<KsAppClientDataNoMeta>;
}): void {
  const page = data.customPagesState.pages[pageId];
  if (!page) {
    throw new Error(`Page not found: ${pageId}`);
  }

  const pageHasTabs = page.tabIds && page.tabIds.length > 0;
  if (pageHasTabs) {
    const newTabIds: string[] = [];
    for (const tabId of page.tabIds) {
      const { newTabId } = duplicateTab({
        tabId,
        data,
      });
      newTabIds.push(newTabId);
    }
    data.customPagesState.pages[newId] = {
      ...page,
      id: newId,
      title: newTitle,
      tabIds: newTabIds,
    };
  } else {
    const { newBlockIds } = duplicateBlocks({
      blockIds: page.blockIds || [],
      data,
    });
    data.customPagesState.pages[newId] = {
      ...page,
      id: newId,
      title: newTitle,
      blockIds: newBlockIds,
    };
  }
}
